import {ContractInfo} from 'components/contract-info/ContractInfo'
import {connectService} from 'components/service/connectService'
import {Sections} from './sections/Sections'
import {useCallback, useContext, useEffect, useMemo} from 'react'
import {NavigationContext} from 'modules/dashboard/NavigationContext'
import {useNavigate, useParams} from 'react-router-dom'
import {Select} from 'components/select/Select'
import {ContractTypes, Roles, getSignedInUser, hasPermission} from 'utility/utility'
import {useConfirm} from 'hooks/useConfirm'
import {Image} from 'components/image/Image'
import {Upload} from 'components/upload/Upload'
import feathers from 'utility/client'
import {useFormik} from 'formik'
import {debounce, keys, pick} from 'lodash'
import {BusinessPrototype, BusinessSchema} from './schema'
import {ValidationError} from 'components/validation-error/ValidationError'
import { useNotification } from 'hooks/useNotification'
import { Licenses } from './components/Licenses'
import { LicenseTypes } from './constants'
import { useDispatch } from 'react-redux'
import { StateActions } from 'store/modules/state/actions'
import { useStatistics } from './hooks/useStatistics'

export const Business = connectService(
  props => {
    const confirm = useConfirm()
    const notify = useNotification()
    const params = useParams()
    const navigate = useNavigate()
    const {setTitle} = useContext(NavigationContext)

    const {data, setData, edit, onSave, remove} = props

    const dispatch = useDispatch()

    useEffect(() => {
      setTitle(edit ? data?.name : 'Create Business')
    }, [data])

    const allowDisablingBusiness = useMemo(() => {
      return edit && hasPermission(Roles.superAdmin) && data?.active
    }, [data, edit])

    const allowEnablingBusiness = useMemo(() => {
      return edit && hasPermission(Roles.superAdmin) && !data?.active
    }, [data, edit])

    const allowDistributorUpdate = useMemo(() => {
      return hasPermission(Roles.superAdmin) && !params.distributor
    }, [params])

    const allowResellerUpdate = useMemo(() => {
      return hasPermission(Roles.distributor) && !params.reseller
    }, [params])

    const allowActions = useMemo(() => {
      const user = getSignedInUser()
      return user.entity !== Roles.business
    }, [])

    const handleRemove = async () => {
      confirm({
        title: `Confirm business deletion `,
        message: `Are you sure you want to remove ${data.name}? All people, device and other access will be removed!`,
        onConfirm: async () => {
          await remove(data._id)
          navigate(-1)
        }
      })
    }

    const handleDeactivation = async () => {
      confirm({
        title: `Confirm business de-activation `,
        message: `Are you sure you want to de-activate ${data.name}? Active users will be disconnected immediately. Furthermore, Administrators will not be able to login`,
        onConfirm: async () => {
          await feathers.service('business').patch(data._id, {
            active: false
          })
          setData({...data, active: false})
          notify({
            notification: `Business ${data.name} has been deactivated.`,
            variant: 'primary'
          })
        }
      })
    }

    const handleReActivation = async () => {
      confirm({
        title: `Confirm business activation `,
        message: `Re activating the business ${data.name} will allow system access again. Are you sure you want to re-activate the business?`,
        onConfirm: async () => {
          await feathers.service('business').patch(data._id, {
            active: true
          })
          setData({...data, active: true})
          notify({
            notification: `Business ${data.name} has been re-activated.`,
            variant: 'primary'
          })
        }
      })
    }

    const onFileChange = data => {
      formik.setFieldValue('logo', data.uri)
    }

    const onFileRemove = () => {
      const [user, fileName] = formik.values.logo.split('/').slice(-2)
      feathers
        .service('media')
        .remove(user, {query: {key: `${user}/${fileName}`}})
        .then(console.log)
        .catch(console.error)
      formik.setFieldValue('logo', undefined)
    }

    const initialData = useMemo(() => {
      return pick({
        ...BusinessPrototype,
        ...data},
        keys(BusinessPrototype)
      )
    }, [data, edit])

    const formik = useFormik({
      initialValues: edit
        ? initialData
        : {
            ...BusinessPrototype
          },
      validationSchema: BusinessSchema,
      onSubmit: async data => {
        if (!data.distributor) {
          delete data.distributor
        }

        if (!data.reseller) {
          delete data.reseller
        }

        if (params.distributor && !allowDistributorUpdate) {
          data.distributor = params.distributor
        }

        if (params.reseller && !allowResellerUpdate) {
          data.reseller = params.reseller
        }

        if (data.contractType === ContractTypes.FreePartnerAccount) {
          delete data.contractExpiry
          delete data.trialExpiry
        } else if (data.contractType === ContractTypes.Customer) {
          delete data.trialExpiry
        } else if (data.contractType === ContractTypes.Trial) {
          delete data.contractExpiry
        } else if (data.contractType === ContractTypes.MonthlyCreditCardCustomer) {
          delete data.contractExpiry
          delete data.trialExpiry
        }

        await onSave({data})
        navigate(-1)
      },
      enableReinitialize: true
    })

    const onUpdate = useCallback(
      debounce(
        (data) => {
        dispatch(StateActions.put({device: data.value.device, state: data.value}))
      },
      200
    ), [])

    const onDelete = useCallback((data) => {
      dispatch(StateActions.remove(data.value.device))
    }, [dispatch])

    const {subscribe, unsubscribe} = useStatistics(data._id, onUpdate, onDelete)

    return (
      <>
        {allowDistributorUpdate && (
          <div className="form-group row">
            <label className="col-sm-4 col-lg-5 col-form-label">Distributor</label>
            <div className="col-sm-8 col-lg-7">
              <Select
                service="distributor"
                title="Select Distributor"
                value={formik.values?.distributor}
                onChange={e => formik.setFieldValue('distributor', e.target.value)}
              />
            </div>
          </div>
        )}

        {allowResellerUpdate && formik.values?.distributor && (
          <div className="form-group row">
            <label className="col-sm-4 col-lg-5 col-form-label">Reseller</label>
            <div className="col-sm-8 col-lg-7">
              <Select
                query={formik.values?.distributor
                  ? {distributor: formik.values.distributor}
                  : {}
                }
                service="reseller"
                title="Select Reseller"
                value={formik.values?.reseller}
                onChange={e => formik.setFieldValue('reseller', e.target.value)}
              />
            </div>
          </div>
        )}

        {allowDisablingBusiness && (
          <div className="row">
            <div className="col-sm-8 col-lg-7 offset-sm-4 offset-lg-5 mt-3">
              <button className="btn btn-primary btn-block" onClick={handleDeactivation}>
              <i className="fal fa-thumbs-down mr-2"></i>De-activate business
              </button>
            </div>
          </div>
        )}

        {allowEnablingBusiness && (
          <div className="row">
            <div className="col-sm-8 col-lg-7 offset-sm-4 offset-lg-5 mt-3">
              <button className="btn btn-primary btn-block" onClick={handleReActivation}>
                <i className="fal fa-thumbs-up mr-2"></i>Re-activate business
              </button>
            </div>
          </div>
        )}

        {
          allowDistributorUpdate ? (
            <>
              <Licenses
                className="row mt-3"
                label="Office device licenses"
                type={formik.values.licenses.device.type}
                limit={formik.values.licenses.device.licenses}
                onLimitChange={e => {
                  formik.setFieldValue('licenses', {
                    ...formik.values.licenses,
                    device: {
                      ...formik.values.licenses.device,
                      licenses: Number(e.target.value)
                    }
                  })
                }}
                onTypeChange={e => {
                  formik.setFieldValue('licenses', {
                    ...formik.values.licenses,
                    device: {
                      type: e.target.value,
                      licenses: e.target.value === LicenseTypes.Unlimited
                        ? formik.values.licenses.device.licenses
                        : 0
                    }
                  })
                }}
                inputProps={{
                  min: 0
                }}
              />

              <Licenses
                className="row mt-3"
                label="Remote device licenses"
                type={formik.values.licenses.remote.type}
                limit={formik.values.licenses.remote.licenses}
                onLimitChange={e => formik.setFieldValue('licenses', {
                  ...formik.values.licenses,
                  remote: {
                    ...formik.values.licenses.remote,
                    licenses: Number(e.target.value)
                  }
                })}
                onTypeChange={e => formik.setFieldValue('licenses', {
                  ...formik.values.licenses,
                  remote: {
                    type: e.target.value,
                    licenses: e.target.value === LicenseTypes.Unlimited
                      ? formik.values.licenses.remote.licenses
                      : 0
                  }
                })}
                inputProps={{
                  min: 0
                }}
              />
            </>
          ) : null
        }

        {(allowDistributorUpdate || allowResellerUpdate || hasPermission(Roles.superAdmin)) && <hr />}

        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Company name</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              placeholder="Company name"
              className="form-control"
              name="name"
              value={formik.values.name}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="name" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Address (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Address"
              name="address"
              value={formik.values.address}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="address" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Postcode (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Postcode"
              name="postalCode"
              value={formik.values.postalCode}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="postalCode" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Phone number (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              className="form-control"
              placeholder="Phone number"
              name="phone"
              value={formik.values.phone}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="phone" />
          </div>
        </div>
        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Business logo (optional)</label>
          <div className="col-sm-8 col-lg-7">
            <Upload onChange={onFileChange} />
            <div className="text-center my-1">or</div>
            <input
              type="text"
              placeholder="Upload via URL"
              className="form-control"
              name="logo"
              value={formik.values.logo}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="logo" />
            <small className="form-text text-muted">
              <i className="fas fa-exclamation-triangle text-warning"></i> Please upload logo image of 775 X
              775 dimensions
            </small>
            <Image src={formik.values.logo} width="100px" height="100px" onClick={onFileRemove} />
          </div>
        </div>

        <div className="form-group row">
          <label className="col-sm-4 col-lg-5 col-form-label">Account Number from ZOHO</label>
          <div className="col-sm-8 col-lg-7">
            <input
              type="text"
              placeholder="Account Number from ZOHO"
              className="form-control"
              name="accountNumber"
              value={formik.values.accountNumber}
              onChange={formik.handleChange}
            />
            <ValidationError formik={formik} property="accountNumber" />
          </div>
        </div>

        {allowActions && (
          <ContractInfo
            contractType={formik.values.contractType}
            contractTypeProps={{name: 'contractType'}}
            onContractTypeChange={formik.handleChange}
            trialExpiry={formik.values.trialExpiry}
            onTrialExpiryChange={value => {
              formik.setFieldValue('trialExpiry', value)
              formik.setFieldValue('contractExpiry', undefined)
            }}
            contractExpiry={formik.values.contractExpiry}
            onContractExpiryChange={value => {
              formik.setFieldValue('trialExpiry', undefined)
              formik.setFieldValue('contractExpiry', value)
            }}
          >
            {((formik.touched.contractType && formik.errors.contractType) ||
              (formik.touched.trialExpiry && formik.errors.trialExpiry) ||
              (formik.touched.contractExpiry && formik.errors.contractExpiry)) && (
              <small className="form-text text-danger">
                {formik.errors.accountNumber}
                {formik.errors.trialExpiry}
                {formik.errors.contractExpiry}
              </small>
            )}
          </ContractInfo>
        )}

        {(hasPermission(Roles.superAdmin)) ? <>
          <hr />
          <div className="form-group row">
            <label className="col-sm-4 col-lg-5 col-form-label">Features</label>
            <div className="col-sm-8 col-lg-7">
              <div className="row gy-2">
                <div className="col-6 col-md-3 form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.options?.whiteboardAccess}
                    onChange={e => formik.setFieldValue('options.whiteboardAccess', e.target.checked)}
                  />
                  <label className="form-check-label">Whiteboard</label>
                </div>
                <div className="col-6 col-md-3  form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.options?.browserAccess}
                    onChange={e => formik.setFieldValue('options.browserAccess', e.target.checked)}
                  />
                  <label className="form-check-label">Browser</label>
                </div>
                <div className="col-6 col-md-3 form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.options?.digitalSignageAccess}
                    onChange={e => formik.setFieldValue('options.digitalSignageAccess', e.target.checked)}
                  />
                  <label className="form-check-label">Digital Signage</label>
                </div>
                <div className="col-6 col-md-3 form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.options?.externalCallingAccess}
                    onChange={e => formik.setFieldValue('options.externalCallingAccess', e.target.checked)}
                  />
                  <label className="form-check-label">External Calling</label>
                </div>
                <div className="col-6 col-md-3 form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    checked={formik.values.options?.videoResolutionsAccess}
                    onChange={e => formik.setFieldValue('options.videoResolutionsAccess', e.target.checked)}
                  />
                  <label className="form-check-label">Video Resolutions</label>
                </div>
              </div>
            </div>
          </div>
          <hr/ >
        </> : null}

        {
          edit && <Sections
            allowActions={allowActions}
            business={data}
          />
        }

        <div className="vw-popup-box__inner__body__btns">
          <button className="btn btn-primary" onClick={formik.handleSubmit}>
            Save business
          </button>

          {allowActions ? (
            <>
              {edit && (
                <button className="btn btn-danger" onClick={handleRemove}>
                  Remove business
                </button>
              )}

              <button className="btn btn-secondary" onClick={() => navigate(-1)}>
                Cancel
              </button>
            </>
          ) : null}
        </div>
      </>
    )
  },
  'business',
  {
    paramsAsQueryParams: ['distributor', 'reseller']
  }
)
